import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { paymentAPI, clientAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { DateRangeFilter, useDateRange } from '../components/DateRangeFilter';
import ReceivePaymentDialog from '../components/ReceivePaymentDialog';
import { formatINR, formatDate, apiError } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Search, Download, Loader2, FileDown, CircleDollarSign, ChevronRight } from 'lucide-react';

// Saves a blob response; a blob-wrapped JSON error is unwrapped so apiError sees `detail`.
async function saveBlob(request, filename) {
  try {
    const res = await request;
    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    const body = err?.response?.data;
    if (body instanceof Blob) {
      try { err.response.data = JSON.parse(await body.text()); } catch { /* not JSON */ }
    }
    toast.error(apiError(err, 'Download failed'));
  }
}

const safe = (s) => (s || '').replace(/[^\w.-]+/g, '_');

export default function PaymentsReceived() {
  const { can } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openIds, setOpenIds] = useState(() => new Set()); // expanded client rows
  const [busy, setBusy] = useState(null); // receipt id | client id | 'all'
  const [recv, setRecv] = useState(null); // { client } | {} (pick in dialog) | null
  const [range, rangeState, setRangeState] = useDateRange('priceintel.paymentsRange', 'this_month');

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await paymentAPI.list({ date_start: range.start || undefined, date_end: range.end || undefined });
      setReceipts(res.data);
    } catch (err) {
      toast.error(apiError(err, 'Failed to fetch payments'));
    } finally {
      setLoading(false);
    }
  }, [range.start, range.end]);

  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  // Clients are only needed by the Receive payment dialog.
  const canWrite = can('write');
  useEffect(() => {
    if (!canWrite) return;
    clientAPI.getAll()
      .then((res) => setClients(res.data))
      .catch((err) => toast.error(apiError(err, 'Failed to load clients')));
  }, [canWrite]);

  // One row per client: their receipts in the period, newest first.
  const groups = useMemo(() => {
    const m = new Map();
    for (const r of receipts) {
      const g = m.get(r.client_id) || { id: r.client_id, name: r.client_name, receipts: [], total: 0, last: '' };
      g.receipts.push(r);
      g.total += r.amount || 0;
      if (r.payment_date > g.last) g.last = r.payment_date;
      m.set(r.client_id, g);
    }
    return [...m.values()].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [receipts]);

  // Search matches the client name, or any of their receipt #, invoice # or reference.
  const q = searchTerm.trim().toLowerCase();
  const shown = !q ? groups : groups.filter((g) => (g.name || '').toLowerCase().includes(q)
    || g.receipts.some((r) => [r.payment_number, r.reference, ...r.invoices.map((x) => x.invoice_number)].join(' ').toLowerCase().includes(q)));
  const total = shown.reduce((s, g) => s + g.total, 0);
  const receiptCount = shown.reduce((s, g) => s + g.receipts.length, 0);

  const period = range.start || range.end ? `${range.start || 'start'}_${range.end || 'today'}` : 'All_time';
  const dates = { date_start: range.start || undefined, date_end: range.end || undefined };

  const toggle = (id) => setOpenIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const run = async (key, fn) => {
    setBusy(key);
    await fn();
    setBusy(null);
  };
  const downloadClient = (g) => run(g.id, () => saveBlob(paymentAPI.statementPdf({ client_id: g.id, ...dates }), `Payments_Received_${safe(g.name)}_${period}.pdf`));
  const downloadAll = () => run('all', () => saveBlob(paymentAPI.statementPdf(dates), `Payments_Received_All_clients_${period}.pdf`));
  const downloadReceipt = (r) => run(r.id, () => saveBlob(paymentAPI.receiptPdf(r.id), `Receipt-${r.payment_number || r.id.slice(0, 8)}_${safe(r.client_name)}_${r.payment_date}.pdf`));

  const spin = (key, Icon, cls = 'w-4 h-4') => (busy === key ? <Loader2 className={`${cls} animate-spin`} /> : <Icon className={cls} />);

  const invoiceLinks = (r) => r.invoices.map((x, i) => (
    <React.Fragment key={x.payment_id}>
      {i > 0 && ', '}
      <Link to={`/invoices/${x.invoice_id}`} className="underline decoration-gray-300 hover:decoration-black">{x.invoice_number}</Link>
      {r.invoices.length > 1 && <span className="text-gray-400"> ({formatINR(x.amount)})</span>}
    </React.Fragment>
  ));

  // A client's receipts, shown when their row is opened.
  const receiptList = (g) => (
    <div className="bg-gray-50 border-t px-4 py-3 space-y-3">
      <ul className="divide-y rounded-lg border bg-white">
        {g.receipts.map((r) => (
          <li key={r.id} className="flex items-center gap-3 px-3 py-2.5">
            <div className="min-w-0 flex-1">
              <p className="text-sm">
                <span className="font-mono text-xs font-medium">#{r.payment_number || '—'}</span>
                <span className="text-gray-500"> · {formatDate(r.payment_date)}</span>
                {(r.method || r.reference) && <span className="text-gray-500"> · {[r.method, r.reference].filter(Boolean).join(' · ')}</span>}
              </p>
              <p className="text-xs text-gray-500 font-mono break-words">Invoice {invoiceLinks(r)}</p>
              <p className="sm:hidden font-mono text-sm mt-0.5">{formatINR(r.amount)}</p>
            </div>
            <p className="hidden sm:block font-mono text-sm whitespace-nowrap">{formatINR(r.amount)}</p>
            <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={() => downloadReceipt(r)} disabled={busy === r.id} aria-label={`Download receipt ${r.payment_number}`}>
              {spin(r.id, Download)}<span className="hidden sm:inline ml-1.5">Receipt</span>
            </Button>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <Button className="bg-black hover:bg-black/90" onClick={() => downloadClient(g)} disabled={busy === g.id}>
          {spin(g.id, FileDown)}<span className="ml-1.5">Download statement</span>
        </Button>
        {canWrite && (
          <Button variant="outline" onClick={() => setRecv({ client: clients.find((c) => c.id === g.id) || { id: g.id, name: g.name } })}>
            <CircleDollarSign className="w-4 h-4 mr-1.5" /> Receive payment
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Payments Received</h1>
          <p className="text-gray-500 mt-1">Pick a period, open a client to see their receipts, and download.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <DateRangeFilter value={rangeState} onChange={setRangeState} />
          <Button variant="outline" onClick={downloadAll} disabled={busy === 'all'}>
            {spin('all', FileDown)}<span className="ml-1.5">All clients PDF</span>
          </Button>
          {canWrite && (
            <Button className="bg-black hover:bg-black/90" onClick={() => setRecv({})}>
              <CircleDollarSign className="w-4 h-4 mr-1.5" /> Receive payment
            </Button>
          )}
        </div>
      </div>

      {/* Total gets the wide card: an all-time total runs to ₹1,00,00,000+. */}
      <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Total received</p>
            <p className="text-2xl font-bold font-mono mt-1 text-green-700">{formatINR(total)}</p>
            <p className="text-xs text-gray-500 mt-1">{range.label === 'Custom' ? `${formatDate(range.start)} – ${formatDate(range.end)}` : range.label}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Clients who paid</p>
            <p className="text-2xl font-bold font-mono mt-1">{shown.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-xs text-gray-500 uppercase tracking-wide">Receipts</p>
            <p className="text-2xl font-bold font-mono mt-1">{receiptCount}</p>
          </CardContent>
        </Card>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search client, receipt #, invoice # or reference"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : shown.length === 0 ? (
            <div className="py-16 text-center text-gray-400 text-sm">No payments received in this period{q ? ' for this search' : ''}.</div>
          ) : (
            <ul className="divide-y" data-testid="payments-client-list">
              <li className="hidden xl:grid grid-cols-[minmax(0,1fr)_90px_120px_150px_130px] gap-3 px-4 py-3 text-sm text-gray-500" aria-hidden="true">
                <span>Client</span>
                <span className="text-right">Receipts</span>
                <span>Last payment</span>
                <span className="text-right">Received</span>
                <span />
              </li>
              {shown.map((g) => {
                const isOpen = openIds.has(g.id);
                return (
                  <li key={g.id}>
                    <div className="flex xl:grid xl:grid-cols-[minmax(0,1fr)_90px_120px_150px_130px] items-center gap-3 px-4 py-3 hover:bg-gray-50">
                      <button
                        type="button"
                        onClick={() => toggle(g.id)}
                        aria-expanded={isOpen}
                        className="flex items-center gap-2 min-w-0 flex-1 text-left"
                      >
                        <ChevronRight className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        <span className="min-w-0">
                          <span className="block font-medium break-words">{g.name}</span>
                          {/* Phones: the amount moves under the name so the name keeps the width. */}
                          <span className="block sm:hidden font-mono text-sm text-green-700">{formatINR(g.total)}</span>
                          <span className="block xl:hidden text-xs text-gray-500">
                            {g.receipts.length} receipt{g.receipts.length === 1 ? '' : 's'} · last {formatDate(g.last)}
                          </span>
                        </span>
                      </button>
                      <span className="hidden xl:block text-right text-gray-600">{g.receipts.length}</span>
                      <span className="hidden xl:block text-gray-600 whitespace-nowrap">{formatDate(g.last)}</span>
                      <span className="hidden sm:block font-mono text-right whitespace-nowrap text-green-700">{formatINR(g.total)}</span>
                      <div className="flex justify-end shrink-0">
                        <Button variant="outline" size="sm" className="h-9" onClick={() => downloadClient(g)} disabled={busy === g.id} aria-label={`Download statement for ${g.name}`} title="Download this client's statement">
                          {spin(g.id, FileDown)}<span className="hidden md:inline ml-1.5">Statement</span>
                        </Button>
                      </div>
                    </div>
                    {isOpen && receiptList(g)}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <ReceivePaymentDialog
        open={!!recv}
        onOpenChange={(o) => { if (!o) setRecv(null); }}
        client={recv?.client}
        clients={clients}
        onSaved={fetchReceipts}
      />
    </div>
  );
}
